'use client';

import classNames from 'classnames';
import Button from 'components/ui-new/Button';
import { Deposit } from 'data/interfaces/Common';
import PurchaseRepository from 'data/repositories/PurchaseRepository';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { MouseEventHandler, useRef } from 'react';
import { LocalStorageKey, MonetizationTariff } from 'types/enums';
import { Environment } from 'utils/getEnv';
import { typedMemo } from 'utils/typedMemo';
import PremiumTariff from './Premium';
import StandardTariff from './Standard';
import styles from './style.module.scss';
import { PremiumTariffForwardRefProps, TariffBaseProps, TariffPropsDiff } from './types';

const Tariff = <T extends MonetizationTariff>({
  variant,
  items,
  scrollTargetRef,
  title,
  ...restProps
}: TariffBaseProps<T>) => {
  const t = useTranslations();
  const params = useSearchParams();
  const premiumRef = useRef<PremiumTariffForwardRefProps>(null);
  const props = restProps as unknown as TariffPropsDiff;

  const handleClickMore: MouseEventHandler<HTMLButtonElement> = (e) => {
    if (props.onClickReadMode) {
      return props.onClickReadMode(e);
    }

    scrollTargetRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleClickBuyPremium = async () => {
    const premiumProducts =
      variant === MonetizationTariff.Premium && premiumRef.current
        ? premiumRef.current.getPremiumProducts()
        : null;
    const valuePremium =
      variant === MonetizationTariff.Premium && premiumRef.current
        ? premiumRef.current.getPremiumValue()
        : null;

    const selectedProduct =
      variant === MonetizationTariff.Premium
        ? premiumProducts[valuePremium]
        : props.standardProduct;
    const order = await PurchaseRepository.createOrder(selectedProduct.id);

    if (!order) {
      throw new Error(`Order with productId ${selectedProduct.id} not created`);
    }

    let deposit: Deposit | null = null;

    if (!Environment.isProd()) {
      deposit = await PurchaseRepository.createDepositTest({
        orderId: order.id,
        currencies: 'USDT',
        symbol: 'USD',
        options: {
          redirectFailedUrl: `${window.location.origin}/failed-payment`,
          redirectSuccessUrl: `${window.location.origin}/success-payment`,
        },
      });
    } else {
      deposit = await PurchaseRepository.createDeposit({
        orderId: order.id,
        amount: String(selectedProduct.contents.totalCount),
        currencies: 'USDT',
        symbol: 'USD',
      });
    }

    localStorage.setItem(LocalStorageKey.editVacancyId, params.get('id') as string);

    window.location.href = deposit.paymentUrl;
  };

  return (
    <div
      className={classNames(styles.container, {
        [styles.container__disable]: props.hideContainer,
      })}>
      <p
        className={classNames(styles.title, {
          [styles.title__disable]: props.hideContainer,
        })}>
        {title ? title : variant === MonetizationTariff.Premium ? 'Premium' : t(variant)}
      </p>
      <div
        className={classNames(styles.inner, {
          [styles.inner__disable]: props.hideContainer,
        })}>
        {variant === 'standard' ? (
          <StandardTariff
            items={items}
            setStandardProduct={props.setStandardProduct}
            standardProduct={props.standardProduct}
            step={props.step}
            useUncontrolled={props.useUncontrolled}
          />
        ) : (
          <PremiumTariff
            ref={premiumRef}
            items={items}
            setPremiumProduct={props.setPremiumProduct}
            hidePerVacancyPrice={props.hidePerVacancyPrice}
          />
        )}
        <div className={styles.btns}>
          <Button
            variant='grey'
            className={classNames(styles.btns__item, styles.btns__item__more)}
            icon={<Image src='/icons/info.svg' alt='info' width={17.5} height={17.5} />}
            onClick={handleClickMore}
            iconPosition='end'>
            {t('more')}
          </Button>
          <Button
            className={classNames(styles.btns__item, {
              [styles.btns__item__prem]: variant === MonetizationTariff.Premium,
              [styles.btns__item__hideContainer]: props.hideContainer,
            })}
            variant='fill'
            onClick={handleClickBuyPremium}>
            {t('shop_buy')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default typedMemo(Tariff);
